export interface OrderSignatureParts {
    prescriptions?: Array<{ storeID?: number | null; rxNumber?: number | null; rfNumber?: number | null }>;
    items?: Array<{ otcItemId?: number | null; quantity?: number | null }>;
    payments?: Array<{ StripeCardId?: string | null; chargeId?: string | null; authorizedAmount?: number | null }>;
    patientAddressId?: number | null;
    deliveryCode?: number | null;
    arAccountId?: number | null;
    arChargeAmount?: number | null;
    arOverrideReason?: string | null;
    storeID?: number | null;
    promisedDate?: string | Date | null;
    transferToPharmacyId?: number | null;
    patientPickupPreference?: boolean | null;
    signatureRequired?: boolean;
    overrideAddress?: boolean;
    disableOneRate?: boolean;
    shipToPrescriber?: boolean;
    overridePriority?: boolean;
    saturdayDelivery?: boolean;
}

function isoOrRaw(value?: string | Date | null): string | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? String(value) : new Date(parsed).toISOString();
}

/** Every field saveEdit sends must appear here, or editing only a missing one leaves the token reusable and the edit is silently adopted away. */
export function orderContentSignature(o: OrderSignatureParts): string {
    return JSON.stringify({
        rx: (o.prescriptions ?? []).map(p => `${p.storeID}-${p.rxNumber}-${p.rfNumber}`).sort(),
        items: (o.items ?? []).map(i => `${i.otcItemId}:${i.quantity}`).sort(),
        // chargeId carries the cash/check tender discriminator, which StripeCardId leaves undefined.
        pay: (o.payments ?? []).map(p => `${p.StripeCardId ?? p.chargeId ?? ""}:${p.authorizedAmount}`).sort(),
        addr: o.patientAddressId ?? null,
        delivery: o.deliveryCode ?? null,
        ar: `${o.arAccountId ?? ""}:${o.arChargeAmount ?? ""}:${o.arOverrideReason ?? ""}`,
        store: o.storeID ?? null,
        promised: isoOrRaw(o.promisedDate),
        transferTo: o.transferToPharmacyId ?? null,
        pickup: o.patientPickupPreference ?? null,
        ship: [o.signatureRequired, o.overrideAddress, o.disableOneRate,
               o.shipToPrescriber, o.overridePriority, o.saturdayDelivery].map(Boolean).join(""),
    });
}

export function generateRequestId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = [...b].map(x => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// 401/403 (single-use positive-ID token) and 418 (order lock) are refused ahead of the save itself.
const REFUSED_BEFORE_SAVE = [401, 403, 418];

/**
 * Whether the request was turned away before the save could run. Nothing was created and no charge was
 * taken, so the token stays valid and asking the server what it created is a wasted round-trip.
 * Deliberately narrow: an ordinary 400 is indistinguishable from a save that threw after committing.
 */
export function refusedBeforeSave(status?: number): boolean {
    return status != null && REFUSED_BEFORE_SAVE.includes(status);
}

/** A 4xx means the charge/validation was rejected (any hold released) → new intent; keep the token on 5xx/network so a retry adopts. */
export function shouldResetTokenOnError(status?: number): boolean {
    if (!status || status < 400 || status >= 500) return false;
    return !refusedBeforeSave(status);
}

export type CreateTokenDecision =
    | { action: "reuse" | "mint"; token: string; signature: string }
    | { action: "adopt-existing"; orderId: number }
    | { action: "abort" };

/**
 * Decides a create submit's token. Probes whenever a prior attempt exists so a committed-but-unacknowledged
 * order is found instead of being duplicated; aborts rather than guessing when the probe itself fails.
 */
export async function resolveCreateTokenWithProbe(
    currentToken: string | null,
    lastSignature: string | null,
    signature: string,
    probe: (token: string) => Promise<number | null>,
    generate: () => string = generateRequestId,
): Promise<CreateTokenDecision> {
    if (!currentToken) return { action: "mint", token: generate(), signature };

    let existingId: number | null;
    try {
        existingId = await probe(currentToken);
    } catch {
        return { action: "abort" };
    }

    if (existingId != null) return { action: "adopt-existing", orderId: existingId };

    return signature === lastSignature
        ? { action: "reuse", token: currentToken, signature }
        : { action: "mint", token: generate(), signature };
}
